import { useEffect, useRef } from "react";
import axios from "axios";
import { getApiUrl } from "@/utils/api";

const API = getApiUrl();
const HEARTBEAT_MS = 20 * 60 * 1000; // 20 min
const JITTER_MS = 30 * 1000;

/**
 * Keeps a long-lived agent/property-admin dashboard session alive by
 * periodically pinging GET /property-admin/me while the tab is visible.
 * The backend already silently rolls the session forward another 30 days
 * whenever it's hit with <7 days left — this hook just makes sure that
 * happens during active use, not only on page reload.
 *
 * Sent with `?heartbeat=true` so the backend's 1-hour INACTIVITY timeout
 * (see useAgentSessionTimeout.js) still applies even while this keeps
 * pinging — a background ping must not count as the agent "being active".
 */
export function useSessionHeartbeat({ onExpired } = {}) {
  const onExpiredRef = useRef(onExpired);
  onExpiredRef.current = onExpired;

  useEffect(() => {
    let timer;
    let stopped = false;
    let inFlight = false;

    const refresh = async () => {
      if (stopped || document.visibilityState !== "visible" || inFlight) return;
      inFlight = true;
      try {
        await axios.get(`${API}/property-admin/me`, { params: { heartbeat: true }, withCredentials: true });
      } catch (err) {
        const status = err?.response?.status;
        if (status === 401 || status === 403) {
          onExpiredRef.current?.();
        }
        // Transient/network errors: leave session alone, retry next tick.
      } finally {
        inFlight = false;
      }
    };

    const schedule = () => {
      clearTimeout(timer);
      if (stopped || document.visibilityState !== "visible") return;
      timer = setTimeout(async () => {
        await refresh();
        schedule();
      }, HEARTBEAT_MS + Math.random() * JITTER_MS);
    };

    const onVisibility = () => {
      if (document.visibilityState === "visible") {
        refresh();
        schedule();
      } else {
        clearTimeout(timer);
      }
    };

    document.addEventListener("visibilitychange", onVisibility);
    schedule();

    return () => {
      stopped = true;
      clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, []);
}
